import { inject, Injectable } from '@angular/core';
import { environment } from '../../../environments/environment';
import { HttpClient } from '@angular/common/http';
import { catchError, Observable, throwError } from 'rxjs';
import {
  Institution,
  InstitutionDetail,
  InstitutionQueryParams,
  MemberQueryParams,
  UploadInstitutionMembersResponse
} from '../../interfaces/responses/institution.interface';
import { PaginationInterface } from '../../interfaces/responses/pagination.interface';
import { toHttpParams } from '../../utils/app.util';
import { InstitutionMembers } from '../../interfaces/responses/institution-members';


const env = environment;

@Injectable({
  providedIn: 'root'
})
export class InstitutionService {

  private http = inject(HttpClient);

  createInstitution(data: FormData): Observable<Institution> {
    let token: string | null = null;
    if (typeof window !== 'undefined') {
      token = localStorage.getItem('tokenn');
    }
    const url = `${env.apiUrl}/institutions`;

    // Only set JSON content type when not sending FormData
    const headers: Record<string, string> = {
      Authorization: `Bearer ${token ?? ''}`,
    };
    if (!(data instanceof FormData)) {
      headers['Content-Type'] = 'application/json';
    }

    return this.http.post<Institution>(url,
      data, { headers },
    ).pipe(
      catchError((error) => {
        return throwError(() => new Error(`Error creating institution ${error}`));
      })
    );
  }

  getInstitutionById(id: string): Observable<InstitutionDetail> {
    let token: string | null = null;
    if (typeof window !== 'undefined') {
      token = localStorage.getItem('tokenn');
    }
    const url = `${env.apiUrl}/institutions/${id}`;
    return this.http.get<InstitutionDetail>(url,
      {
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        }
      },
    ).pipe(
      catchError((error) => {
        return throwError(() => new Error(`Error creating institution ${error}`));
      })
    );
  }

  getPaginatedInstitutions(queryParams: InstitutionQueryParams): Observable<PaginationInterface<Institution[]>> {
    let token: string | null = null;
    if (typeof window !== 'undefined') {
      token = localStorage.getItem('tokenn');
    }
    const url = `${env.apiUrl}/institutions`;
    return this.http.get<PaginationInterface<Institution[]>>(url, {
      params: toHttpParams(queryParams),
      headers: {
        Authorization: `Bearer ${token}`,
      }
    }).pipe(
      catchError((error) => {
        return throwError(() => new Error(`Error fetching institutions ${error}`));
      })
    );
  }

  deleteInstitution(id: string): Observable<Institution> {
    let token: string | null = null;
    if (typeof window !== 'undefined') {
      token = localStorage.getItem('tokenn');
    }
    const url = `${env.apiUrl}/institutions/${id}`;
    return this.http.delete<Institution>(url, {
      headers: {
        Authorization: `Bearer ${token}`,
      }
    }).pipe(
      catchError((error) => {
        return throwError(() => new Error(`Error deleting institution ${error}`));
      })
    );
  }

  updateInstitution(id: string, data: FormData): Observable<Institution> {
    let token: string | null = null;
    if (typeof window !== 'undefined') {
      token = localStorage.getItem('tokenn');
    }
    const url = `${env.apiUrl}/institutions/${id}`;

    // Only set JSON content type when not sending FormData
    const headers: Record<string, string> = {
      Authorization: `Bearer ${token ?? ''}`,
    };
    if (!(data instanceof FormData)) {
      headers['Content-Type'] = 'application/json';
    }

    return this.http.put<Institution>(url,
      data, { headers },
    ).pipe(
      catchError((error) => {
        return throwError(() => new Error(`Error updating institution ${error}`));
      })
    );
  }

  toggleStatus(id: string): Observable<Institution> {
    let token: string | null = null;
    if (typeof window !== 'undefined') {
      token = localStorage.getItem('tokenn');
    }
    const url = `${env.apiUrl}/institutions/${id}/status`;
    return this.http.patch<Institution>(url, {}, {
      headers: {
        Authorization: `Bearer ${token}`,
        'Content-Type': 'application/json',
      }
    }).pipe(
      catchError((error) => {
        return throwError(() => new Error(`Error toggling institution status ${error}`));
      })
    );
  }

  postInstitutionMembers(data: FormData): Observable<UploadInstitutionMembersResponse> {
    let token: string | null = null;
    if (typeof window !== 'undefined') {
      token = localStorage.getItem('tokenn');
    }
    const url = `${env.apiUrl}/institutions/members`;
    return this.http.post<UploadInstitutionMembersResponse>(url,
      data, {
      headers: {
        Authorization: `Bearer ${token}`,
      }
    },
    ).pipe(
      catchError((error) => throwError(() => error))
    );
  }

  getMembers(queryParams: MemberQueryParams): Observable<PaginationInterface<InstitutionMembers[]>> {
    let token: string | null = null;
    if (typeof window !== 'undefined') {
      token = localStorage.getItem('tokenn');
    }
    const url = `${env.apiUrl}/institutions/members`;
    return this.http.get<PaginationInterface<InstitutionMembers[]>>(url, {
      params: toHttpParams(queryParams),
      headers: {
        Authorization: `Bearer ${token}`,
      }
    }).pipe(
      catchError((error) => {
        return throwError(() => new Error(`Error fetching members ${error}`));
      })
    );
  }

  toggleMemberStatus(institutionId: string, userId: string): Observable<InstitutionMembers> {
    let token: string | null = null;
    if (typeof window !== 'undefined') {
      token = localStorage.getItem('tokenn');
    }
    const url = `${env.apiUrl}/institutions/${institutionId}/members/${userId}/toggle-status`;
    return this.http.post<InstitutionMembers>(url, {}, {
      headers: {
        Authorization: `Bearer ${token}`,
        'Content-Type': 'application/json',
      }
    }).pipe(
      catchError((error) => {
        return throwError(() => new Error(`Error toggling member status ${error}`));
      })
    );
  }

  deleteMember(institutionId: string, memberId: string): Observable<any> {
    let token: string | null = null;
    if (typeof window !== 'undefined') {
      token = localStorage.getItem('tokenn');
    }
    const url = `${env.apiUrl}/institutions/${institutionId}/members/${memberId}`;
    return this.http.delete<any>(url, {
      headers: {
        Authorization: `Bearer ${token}`,
      }
    }).pipe(
      catchError((error) => {
        return throwError(() => new Error(`Error deleting member ${error}`));
      })
    );
  }

  updateMember(institutionId: string, memberId: string, data: Record<string, any>): Observable<InstitutionMembers> {
    let token: string | null = null;
    if (typeof window !== 'undefined') {
      token = localStorage.getItem('tokenn');
    }
    const url = `${env.apiUrl}/institutions/${institutionId}/members/${memberId}`;
    return this.http.put<InstitutionMembers>(url, data, {
      headers: {
        Authorization: `Bearer ${token ?? ''}`,
        'Content-Type': 'application/json',
      }
    }).pipe(
      catchError((error) => throwError(() => error))
    );
  }

  resendIdentityVerification(data: { userId: string, identityType: string }): Observable<any> {
    let token: string | null = null;
    if (typeof window !== 'undefined') {
      token = localStorage.getItem('tokenn');
    }
    const url = `${env.apiUrl}/identity-verifications/resend`;
    return this.http.post<any>(url, data, {
      headers: {
        Authorization: `Bearer ${token}`,
        'Content-Type': 'application/json',
      }
    }).pipe(
      catchError((error) => {
        return throwError(() => new Error(`Error resending verification ${error}`));
      })
    );
  }
}
